import { keepPreviousData, QueryKey, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { Either } from '@playq/irt';
import { AppID, Environment, Filter, TimePrecision } from '@playq/octopus-common';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import {
  IUseQueryOptionsExtended,
  useEitherQuery,
  useMutateQueryDataAfterEntityDeleting,
  useQueryPrefetch,
} from '/api/service-hooks';
import { IBaseSort } from '/common/models';
import { appToolkit } from '/store';
import { toMs } from '/helpers';

import { createMutateUpdated, createMutateUpdatedBatchStatus } from './cache';
import { getEnabledOrPrefetchStatus } from './helpers';
import { useDeleteCacheBatch } from './useDeleteCacheBatch';
/**
 * @template R - a successful response type.
 * @template ID - entity ID type.
 * @template E - entity type.
 * @template S - entity serialized.
 * @template T - sort type.
 */
export const useQueryBase = <
  R extends { total: number },
  E extends { totalNotes: number },
  ID extends { serialize: () => string },
  T extends IBaseSort<string>,
  S extends { id: string },
>(
  keys: QueryKey,
  env: Environment,
  service: (
    appID: AppID,
    iterator: OffsetLimit,
    sortBy: T[],
    filterBy: {
      [key: string]: Filter;
    },
    env: Environment
  ) => Promise<Either<GenericFailure, R | undefined>>,
  query: {
    iterator: OffsetLimit;
    sortBy: T[];
    filterBy: {
      [key: string]: Filter;
    };
  },
  getID: (entity?: E) => ID | undefined,
  getEntities: (response: R) => E[],
  setEntities: (response: R, entities: E[]) => void,
  responseClass: (prevResponse: R) => R,
  getSerialize: (response: R) => S[],
  responseCs: (newArray: S[], updatedIterator: OffsetLimit, updatedTotal: number) => R,
  options?: IUseQueryOptionsExtended<R | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID) as AppID;
  const queryClient = useQueryClient();

  const { sortBy, iterator, filterBy } = query;
  const queryKeys: QueryKey = [...keys, appID, iterator, sortBy, filterBy, env];
  const enabled = getEnabledOrPrefetchStatus(options?.enabled, appID);
  const enablePrefetch = getEnabledOrPrefetchStatus(options?.enablePrefetch, appID);

  const { mutate, refetch, ...res } = useEitherQuery(queryKeys, () => service(appID, iterator, sortBy, filterBy, env), {
    placeholderData: keepPreviousData,
    staleTime: toMs(TimePrecision.Minute, 10),
    enabled,
    ...options,
  });
  const entities = getEntities(res.data || ({} as R));
  const total = res.data?.total ?? 0;

  const { nextKeys, nextIterator, preFetchData } = useQueryPrefetch({
    keys: queryKeys,
    enabled: enablePrefetch,
    total,
    args: [appID, iterator, sortBy, filterBy, env],
    serviceMethod: service,
  });
  const mutateDeletedEntity = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    nextPageQueryKey: nextKeys,
    total,
    iterator,
    mutate,
    refetch,
    getID,
    getEntities,
    setEntities,
    createNewResponse: (prevResponse) => responseClass(prevResponse),
  });
  const removeItemBatch = useDeleteCacheBatch({
    mutate,
    total,
    nextKeys,
    getSerialize,
    responseClass: responseCs,
    queryClient,
  });

  const mutateUpdated = createMutateUpdated(mutate);
  const mutateUpdatedBatchStatus = createMutateUpdatedBatchStatus(mutate);

  return {
    ...res,
    nextIterator,
    nextKeys,
    preFetchData,
    queryKeys,
    entities,
    total,
    refetch,
    mutateDeletedEntity,
    mutateUpdated,
    mutateUpdatedBatchStatus,
    removeItemBatch,
  };
};
